import { Alert, Segmented, Switch, message } from "antd";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import SwitchCourseBoard from "../../../components/montessori-course-board/SwitchCourseBoard";
import { selectUser } from "../../../redux/slices/auth.slice";
import {
  fetchGroupSettings,
  saveGroupSettings,
} from "../../../services/montessoriCourseBoard.service";
import useGroupBranchesByType from "../useGroupBranchesByType";
import GroupBranchClasses from "./GroupBranchClasses";

/**
 * The group's Junior course board page, in two sections.
 *
 * Group Board: authored once here and read by every Montessori branch in the
 * group. A group row is one shared record, not a copy per branch, so there is no
 * branch picker and no `targetSchools` — it goes to the whole group by definition.
 * Branch admins see it read-only, badged "Group", alongside their own content.
 *
 * Branch Classes: pick one Junior school, see its classes, and open a class's own
 * board. That one switches into the branch, unlike GroupSeniorCourseBoard.js which
 * is only a branch switcher.
 */
const GroupJuniorCourseBoard = () => {
  const user = useSelector(selectUser);
  const groupId = user?.group;
  const { options, isLoading } = useGroupBranchesByType(true);
  const queryClient = useQueryClient();

  // Two sections on one page: the board authored for the whole group, and a
  // browser for one branch's classes. Group Board is what the page always was.
  const [view, setView] = useState("group");

  const settingsKey = ["montessori-group-settings", groupId];
  const settings = useQuery(settingsKey, () => fetchGroupSettings(groupId), {
    enabled: !!groupId,
  });

  const toggleViewOnly = useMutation(
    (viewOnly) => saveGroupSettings(groupId, viewOnly),
    {
      onSuccess: (result) => {
        queryClient.setQueryData(settingsKey, result);
        message.success(
          result?.view_only
            ? "Branches can now only view. Downloads are off."
            : "Branches can download again.",
        );
      },
      onError: (err) => {
        message.error(
          err?.response?.data?.error?.errors?.[0] || "Could not save the setting",
        );
      },
    },
  );

  if (!groupId) {
    return (
      <div className="p-8">
        <Alert
          type="warning"
          showIcon
          message="No school group"
          description="This account is not attached to a school group, so there is no group course board to manage."
        />
      </div>
    );
  }

  return (
    <div>
      <div className="px-8 pt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className="bg-white drop-shadow-md p-8 w-full"
        >
          <div className="text-2xl text-orange-500 font-semibold pb-1">
            Junior Course Board
          </div>
          <div className="text-sm text-gray-500 pb-4">
            Publish once for every Junior branch, or open a branch's own class
            boards.
          </div>

          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: "Group Board", value: "group" },
              { label: "Branch Classes", value: "branches" },
            ]}
          />
        </div>
      </div>

      {view === "branches" ? (
        <div className="px-8 pt-6">
          <GroupBranchClasses />
        </div>
      ) : (
        <>
          <div className="px-8 pt-6">
            <div
              style={{ borderRadius: "10px", color: "#616161" }}
              className="bg-white drop-shadow-md p-8 w-full"
            >
              <div className="text-sm text-gray-500 pb-4">
                Everything you publish here appears on every class board at every
                Junior branch, and branch admins cannot change it. Each branch can
                still add its own content alongside yours.
                {!isLoading && options.length > 0 && (
                  <>
                    {" "}
                    Appears at {options.length} Junior branch
                    {options.length > 1 ? "es" : ""}.
                  </>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 p-4">
                <div>
                  <div className="text-sm font-semibold">
                    View only for branches
                  </div>
                  <div className="text-xs text-gray-500">
                    Branch admins and families can view the Schedule and
                    Curriculum but cannot download them. You can always
                    download.
                  </div>
                </div>
                <Switch
                  checked={!!settings.data?.view_only}
                  loading={settings.isLoading || toggleViewOnly.isLoading}
                  onChange={(checked) => toggleViewOnly.mutate(checked)}
                />
              </div>
            </div>
          </div>

          <SwitchCourseBoard
            scope="group"
            groupId={groupId}
            showBackButton={false}
          />
        </>
      )}
    </div>
  );
};

export default GroupJuniorCourseBoard;
